import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Box } from '@mui/material';
import Drawer from 'features/IRWorkstation/components/Common/Drawer/Drawer';
import DraftOutreachSection from 'features/IRWorkstation/components/DraftOutreach/DraftOutreachSection';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';

// Draft outreach for an LP without an alert: the drafting endpoint accepts an
// Account id directly, so the section from the alerts drawer is reused with
// its in-section label hidden (the drawer header already says "Draft Outreach").
// Children mount only while open so the topic/draft reset on each open.
const DraftOutreachDrawer = ({ accountId, accountName, open, onClose }) => {
	const { tenant, featureFlags } = useIRWorkstation();
	const { trackButtonClick, trackPageLoad } = useTrackEvent({
		tenant,
		product: 'IEM',
	});

	const isDraftOutreachEnabled =
		featureFlags?.['enable-draft-outreach-prompt'] !== false;

	useEffect(() => {
		if (open && isDraftOutreachEnabled) {
			trackPageLoad(PageLoadsTargets.DraftOutreachDrawer);
		}
	}, [open, isDraftOutreachEnabled]);

	const handleClose = () => {
		trackButtonClick(ButtonClickTargets.LPDraftOutreachClose);
		onClose();
	};

	if (!isDraftOutreachEnabled) {
		return null;
	}
	
	return (
		<Drawer
			open={open}
			onClose={handleClose}
			title='Draft Outreach'
			subtitle={accountName}
		>
			<Box sx={{ padding: '16px 18px' }}>
				{open && accountId && (
					<DraftOutreachSection
						tenantId={tenant?.id}
						salesforceObjectType='Account'
						salesforceObjectId={accountId}
						salesforceOrgUrl={tenant?.salesforce_org_url}
						showSectionLabel={false}
						onGenerateClick={() =>
							trackButtonClick(ButtonClickTargets.LPDraftOutreachGenerate)
						}
						onApplyClick={() => {
							trackButtonClick(ButtonClickTargets.LPDraftOutreachApply);
						}}
						onViewInCrmClick={() => {
							trackButtonClick(ButtonClickTargets.LPDraftOutreachViewRecordInCrm);
						}}
						onCopyClick={() => {
							trackButtonClick(ButtonClickTargets.LPDraftOutreachCopy);
						}}
					/>
				)}
			</Box>
		</Drawer>
	);
};

DraftOutreachDrawer.propTypes = {
	accountId: PropTypes.string.isRequired,
	accountName: PropTypes.string,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default DraftOutreachDrawer;
